import { useCallback, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { getLanguageName } from '../utils/constants/languageMapping';
import enTranslations from '../locales/en.json';

type TranslationData = Record<string, any>;

// Load locale with automatic fallback to English
async function loadLocale(langCode: string): Promise<{ translations: TranslationData; langCode: string }> {
  const normalized = langCode.toLowerCase().trim();
  
  try {
    const module = await import(`../locales/${normalized}.json`);
    return { translations: module.default || module, langCode: normalized };
  } catch {
    console.warn(`Locale '${normalized}' not found, falling back to 'en'`);
    return { translations: enTranslations, langCode: 'en' };
  }
}

// Determine language with priority chain
function resolveLanguage(
  preferred?: string,
  fallback?: string
): string {
  return (preferred || fallback || 'en').toLowerCase().trim();
}

// Safely get nested translation value
function getNestedValue(obj: any, path: string): string {
  return path.split('.').reduce((current, key) => 
    current?.[key], obj
  ) ?? path;
}

export const useTranslation = () => {
  const userDetails = useSelector((state: RootState) => state.userDetails.userDetails);
  const siteConfig = useSelector((state: RootState) => state.userDetails.siteConfig);

  const [translations, setTranslations] = useState<TranslationData>(enTranslations);
  const [currentLanguage, setCurrentLanguage] = useState<string>('en');
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef<string | null>(null);
  const manualChangeRef = useRef(false); // Track manual changes

  // Resolve language from Redux state
  const resolvedLanguage = resolveLanguage(
    userDetails?.preferredLanguage,
    siteConfig?.languageConfig?.DefaultLanguage
  );

  // Load translations when language changes (only from Redux, not manual changes)
  useEffect(() => {
    // Skip if this was a manual change
    if (manualChangeRef.current) {
      manualChangeRef.current = false;
      return;
    }

    // Prevent duplicate loads
    if (loadingRef.current === resolvedLanguage || currentLanguage === resolvedLanguage) return;
    
    loadingRef.current = resolvedLanguage;
    setIsLoading(true);
    
    loadLocale(resolvedLanguage)
      .then(({ translations: loadedTranslations, langCode }) => {
        setTranslations(loadedTranslations);
        setCurrentLanguage(langCode);
      })
      .catch((error) => {
        console.error(`Failed to load locale '${resolvedLanguage}':`, error);
        setTranslations(enTranslations);
        setCurrentLanguage('en');
      })
      .finally(() => {
        setIsLoading(false);
        loadingRef.current = null;
      });
  }, [resolvedLanguage, currentLanguage]);

  // Translation function
  const t = useCallback(
    (key: string): string => {
      const value = getNestedValue(translations, key);
      return typeof value === 'string' ? value : key;
    },
    [translations]
  );

  // Function to change language immediately (optimistic update)
  const changeLanguage = useCallback(async (langCode: string) => {
    const normalized = langCode.toLowerCase().trim();
    
    // Mark this as a manual change to prevent useEffect from reloading
    manualChangeRef.current = true;
    
    setIsLoading(true);
    try {
      const { translations: newTranslations, langCode: actualLangCode } = await loadLocale(normalized);
      setTranslations(newTranslations);
      setCurrentLanguage(actualLangCode);
      return actualLangCode;
    } catch (error) {
      console.error(`Failed to change language to '${normalized}':`, error);
      manualChangeRef.current = false; // Reset on error
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get available languages from config
  const getAvailableLanguages = useCallback(() => {
    const supported = siteConfig?.languageConfig?.SupportedLanguages;
    return supported?.map((lang: string) => ({
      id: lang,
      name: getLanguageName(lang)
    })) || [];
  }, [siteConfig?.languageConfig?.SupportedLanguages]);

  return { 
    t, 
    changeLanguage,
    getAvailableLanguages,
    currentLanguage,
    isLoading
  };
};

